import { cn } from "@/lib/utils";
import { VoteComment } from "@/types/vote";
import { AnonymousProfile } from "@/utils/anonymousProfile";
import Avatar from "boring-avatars";
import Image from "next/image";
import { useCallback, useEffect, useRef, useState } from "react";

interface ReplyInputProps {
    parentComment: VoteComment;
    isAnonymousMode: boolean;
    anonymousProfile: AnonymousProfile | null;
    user: { id: string; name: string; profileImg: string } | null;
    isSubmitting: boolean;
    onSubmit: (text: string) => Promise<void>;
    onCancel: () => void;
    /** 답글의 답글인 경우, 멘션할 사용자 이름 */
    mentionUsername?: string;
}

/**
 * 인라인 답글 입력 컴포넌트
 * 특정 댓글에 대한 답글을 작성할 수 있는 컴팩트한 폼
 */
export const ReplyInput: React.FC<ReplyInputProps> = ({
    parentComment,
    isAnonymousMode,
    anonymousProfile,
    user,
    isSubmitting,
    onSubmit,
    onCancel,
    mentionUsername
}) => {
    // 텍스트만 저장 (멘션은 별도 표시)
    const [replyText, setReplyText] = useState("");
    const [isEntered, setIsEntered] = useState(false);
    const [isSubmittingAnimation, setIsSubmittingAnimation] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // 마운트 시 entering 애니메이션 트리거 및 포커스
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsEntered(true);
            setTimeout(() => {
                textareaRef.current?.focus();
            }, 200);
        }, 50);
        return () => clearTimeout(timer);
    }, []);

    // 바깥 클릭 시 닫기
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                onCancel();
            }
        };

        // 약간의 딜레이 후 리스너 등록 (열릴 때 클릭 방지)
        const timer = setTimeout(() => {
            document.addEventListener("mousedown", handleClickOutside);
        }, 100);

        return () => {
            clearTimeout(timer);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [onCancel]);

    // 부모 댓글 작성자 이름 가져오기
    const getParentAuthorName = () => {
        if (parentComment.is_anonymous && parentComment.anonymous_nickname) {
            return parentComment.anonymous_nickname;
        }
        return parentComment.mix_user?.name || "알 수 없음";
    };

    const handleSubmit = useCallback(async () => {
        if (!replyText.trim() || isSubmitting) return;

        setIsSubmittingAnimation(true);
        try {
            // 멘션이 있으면 앞에 붙여서 제출
            const finalText = mentionUsername
                ? `@${mentionUsername} ${replyText}`
                : replyText;
            await onSubmit(finalText);
            setReplyText("");
            setTimeout(() => {
                setIsSubmittingAnimation(false);
                onCancel(); // 제출 후 입력 폼 닫기
            }, 300);
        } catch (error) {
            setIsSubmittingAnimation(false);
            // 에러는 상위 컴포넌트에서 처리
        }
    }, [replyText, isSubmitting, onSubmit, onCancel, mentionUsername]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
            }
            if (e.key === "Escape") {
                onCancel();
            }
        },
        [handleSubmit, onCancel]
    );

    return (
        <div
            ref={containerRef}
            className={cn(
                "mb-4 mt-2 pl-0 transition-all duration-300 md:pl-6",
                isEntered
                    ? "translate-y-0 opacity-100"
                    : "-translate-y-3 opacity-0"
            )}
            style={{
                transitionTimingFunction: "cubic-bezier(0.33, 1, 0.68, 1)"
            }}
        >
            {/* 프로필 미리보기 + 답글 대상 */}
            <div className="mb-2 flex items-center gap-2">
                {isAnonymousMode && anonymousProfile ? (
                    <>
                        <Avatar
                            name={anonymousProfile.avatarSeed}
                            variant="beam"
                            size={24}
                            className="select-none"
                        />
                        <span
                            className="text-xs font-medium text-gray-600
                                dark:text-gray-400"
                        >
                            {anonymousProfile.nickname}
                        </span>
                        <span
                            className="text-xs text-gray-500 dark:text-gray-500"
                        >
                            (익명)
                        </span>
                    </>
                ) : (
                    <>
                        {user?.profileImg ? (
                            <Image
                                src={user.profileImg}
                                alt={user.name}
                                className="rounded-full object-cover"
                                width={24}
                                height={24}
                            />
                        ) : (
                            <div
                                className="flex h-6 w-6 items-center
                                    justify-center rounded-full bg-gray-300
                                    text-xs font-semibold text-gray-600
                                    dark:bg-gray-700 dark:text-gray-300"
                            >
                                {user?.name?.charAt(0)}
                            </div>
                        )}
                        <span
                            className="text-xs font-medium text-gray-600
                                dark:text-gray-400"
                        >
                            {user?.name}
                        </span>
                    </>
                )}
                {/* 답글 대상 표시 */}
                <span className="text-xs text-gray-400 dark:text-gray-500">
                    →
                </span>
                <span
                    className="text-xs font-medium text-blue-600
                        dark:text-blue-400"
                >
                    {mentionUsername || getParentAuthorName()}
                </span>
                <span className="text-xs text-gray-400 dark:text-gray-500">
                    에게 답글
                </span>
            </div>

            {/* 답글 입력 폼 */}
            <div
                className={cn(
                    "flex transition-all duration-300",
                    isSubmittingAnimation && "animate-comment-submit"
                )}
            >
                <textarea
                    ref={textareaRef}
                    value={replyText}
                    onChange={(e) => {
                        setReplyText(e.target.value);
                        // Auto-resize height
                        e.target.style.height = "auto";
                        e.target.style.height = `${Math.min(
                            e.target.scrollHeight,
                            120
                        )}px`;
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="답글을 입력하세요..."
                    disabled={isSubmitting}
                    rows={1}
                    className="flex-1 resize-none rounded-lg border
                        border-gray-200 bg-white px-3 py-2 text-sm text-black
                        transition-all duration-300 focus:border-gray-500
                        focus:outline-none disabled:text-gray-400
                        dark:border-gray-700 dark:bg-gray-800 dark:text-white
                        dark:disabled:text-gray-500"
                    style={{ minHeight: "36px", maxHeight: "120px" }}
                />

                {/* 등록 버튼 - 텍스트 있을 때만 나타남 */}
                <button
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className={cn(
                        `whitespace-nowrap rounded-lg bg-gray-900 py-2 text-sm
                        font-bold text-white transition-all duration-300
                        ease-in-out hover:-translate-y-0.5 hover:shadow-md
                        hover:shadow-gray-500/40 disabled:cursor-not-allowed
                        disabled:bg-gray-300 disabled:shadow-none dark:bg-white
                        dark:text-black dark:disabled:bg-gray-900`,
                        replyText.trim() === ""
                            ? `pointer-events-none w-0 scale-0 overflow-hidden
                                opacity-0`
                            : "pointer-events-auto ml-2 w-auto px-4 opacity-100"
                    )}
                >
                    {isSubmitting ? "등록 중..." : "등록"}
                </button>
            </div>

            {/* 키보드 힌트 */}
            <div className="mt-1 text-[10px] text-gray-400 dark:text-gray-500">
                <kbd
                    className="rounded bg-gray-100 px-1 py-0.5 text-[10px]
                        dark:bg-gray-800"
                >
                    Enter
                </kbd>{" "}
                등록 |{" "}
                <kbd
                    className="rounded bg-gray-100 px-1 py-0.5 text-[10px]
                        dark:bg-gray-800"
                >
                    Shift + Enter
                </kbd>{" "}
                줄바꿈 |{" "}
                <kbd
                    className="rounded bg-gray-100 px-1 py-0.5 text-[10px]
                        dark:bg-gray-800"
                >
                    ESC
                </kbd>{" "}
                또는 바깥 클릭으로 취소
            </div>
        </div>
    );
};
